import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { FastField, Field, Form, Formik } from 'formik';
import Checkbox from '@/trio-ui/Checkbox';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import { Months, getYearsFromNowList } from '@/utils/dates';
import { buildErrorMessage, educationSchema } from '@/utils/formsValidation';
import * as Styles from './CandidateEducationItem.styles';

export type CandidateEducationItemComponentProps = {
  id?: string;
  university: string;
  degree: string;
  endMonth: string;
  endYear: string;
  startMonth: string;
  startYear: string;
  currentlyPursuing: boolean;
  onSaveEducation: (values: any) => void;
  onCancelEducationEdition: () => void;
  isEditing: boolean;
};

const CandidateEducationItemComponent = ({
  id,
  university,
  degree,
  onSaveEducation,
  onCancelEducationEdition,
  isEditing,
  endMonth,
  endYear,
  startMonth,
  startYear,
  currentlyPursuing,
}: CandidateEducationItemComponentProps): JSX.Element => {
  return (
    <Styles.Box>
      <Formik
        validationSchema={educationSchema}
        initialValues={{
          id,
          university,
          degree,
          endMonth,
          endYear,
          startMonth,
          startYear,
          currentlyPursuing,
        }}
        onSubmit={(values) => onSaveEducation(values)}
      >
        {(form) => {
          return (
            <Form>
              <Styles.Row>
                <Styles.FieldContainer>
                  <FastField name="university">
                    {({ field }) => (
                      <InputText
                        value={field.value}
                        name="university"
                        label="University/School"
                        errorMessage={buildErrorMessage(
                          form.touched.university,
                          form.errors.university
                        )}
                        onChangeHandler={form.handleChange}
                        isLabelAboveField
                      />
                    )}
                  </FastField>
                </Styles.FieldContainer>
                <Styles.FieldContainer>
                  <FastField name="degree">
                    {({ field }) => (
                      <InputText
                        isDisabled={!isEditing}
                        value={field.value}
                        name="degree"
                        label="Degree Type"
                        errorMessage={buildErrorMessage(form.touched.degree, form.errors.degree)}
                        onChangeHandler={form.handleChange}
                        isLabelAboveField
                      />
                    )}
                  </FastField>
                </Styles.FieldContainer>
              </Styles.Row>

              <Styles.Row>
                <Styles.TwoColumnsContainer>
                  <Styles.DateDropdownFieldContainer flatSide="right">
                    <Field name="startMonth">
                      {({ field }) => (
                        <DropDown
                          label="From"
                          name="startMonth"
                          isLabelAboveField
                          defaultValue={field.value}
                          errorMessage={buildErrorMessage(
                            form.touched.startMonth,
                            form.errors.startMonth
                          )}
                          onChangeDropDown={form.handleChange}
                          options={Object.keys(Months)}
                        />
                      )}
                    </Field>
                  </Styles.DateDropdownFieldContainer>
                  <Styles.DateDropdownFieldContainer flatSide="left">
                    <Field name="startYear">
                      {({ field }) => (
                        <DropDown
                          label=" "
                          name="startYear"
                          isLabelAboveField
                          defaultValue={field.value}
                          errorMessage={buildErrorMessage(
                            form.touched.startYear,
                            form.errors.startYear
                          )}
                          onChangeDropDown={form.handleChange}
                          options={getYearsFromNowList()}
                        />
                      )}
                    </Field>
                  </Styles.DateDropdownFieldContainer>
                </Styles.TwoColumnsContainer>
                <Styles.TwoColumnsContainer>
                  <Styles.DateDropdownFieldContainer flatSide="right">
                    <Field name="endMonth">
                      {({ field }) => (
                        <DropDown
                          label="To"
                          name="endMonth"
                          isLabelAboveField
                          defaultValue={field.value}
                          errorMessage={buildErrorMessage(
                            form.touched.endMonth,
                            form.errors.endMonth
                          )}
                          onChangeDropDown={form.handleChange}
                          options={Object.keys(Months)}
                          isDisabled={form.values.currentlyPursuing}
                        />
                      )}
                    </Field>
                  </Styles.DateDropdownFieldContainer>
                  <Styles.DateDropdownFieldContainer flatSide="left">
                    <Field name="endYear">
                      {({ field }) => (
                        <DropDown
                          label=" "
                          name="endYear"
                          isLabelAboveField
                          errorMessage={buildErrorMessage(
                            form.touched.endYear,
                            form.errors.endYear
                          )}
                          defaultValue={field.value}
                          onChangeDropDown={form.handleChange}
                          options={getYearsFromNowList()}
                          isDisabled={form.values.currentlyPursuing}
                        />
                      )}
                    </Field>
                  </Styles.DateDropdownFieldContainer>
                </Styles.TwoColumnsContainer>
              </Styles.Row>

              <Field name="currentlyPursuing">
                {({ field }) => (
                  <Checkbox
                    name="currentlyPursuing"
                    label="Currently pursuing"
                    checked={field.value}
                    onChangeHandler={() => {
                      form.setValues({
                        ...form.values,
                        currentlyPursuing: !form.values.currentlyPursuing,
                        endMonth: 'Month',
                        endYear: 'Year',
                      });
                    }}
                  />
                )}
              </Field>

              <div className="flex" style={{ gap: 16 }}>
                <TransparentButton isBlueButton icon={SaveIcon} type="submit">
                  Save
                </TransparentButton>
                <TransparentButton
                  type="button"
                  icon={DeleteIcon}
                  isRedButton
                  onClick={() => {
                    onCancelEducationEdition();
                    form.resetForm();
                  }}
                >
                  Cancel
                </TransparentButton>
              </div>

              <Styles.Divider />
            </Form>
          );
        }}
      </Formik>
    </Styles.Box>
  );
};

export default CandidateEducationItemComponent;
